import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { subscriptionApi } from '../lib/api';
import { Dialog, DialogContent } from './ui/dialog';
import { Crown } from 'lucide-react';

export function SubscriptionGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    checkTrialStatus();
  }, [location.pathname]);

  const checkTrialStatus = async () => {
    // Don't show paywall on abbonamento page - let user complete payment
    if (location.pathname === '/abbonamento') {
      setShowPaywall(false);
      setLoading(false);
      return;
    }
    
    try {
      const response = await subscriptionApi.getTrialStatus();
      setTrialStatus(response.data);
      
      // SuperUsers never see the paywall
      if (response.data.is_superuser) {
        setShowPaywall(false);
        setLoading(false);
        return;
      }
      
      // Show paywall if trial expired and no subscription
      if (response.data.status === 'expired' && response.data.needs_subscription) {
        setShowPaywall(true);
      } else {
        setShowPaywall(false);
      }
    } catch (error) {
      console.error('Error checking trial status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToSubscription = () => {
    setShowPaywall(false); // Hide paywall first
    // Use window.location for reliable navigation
    window.location.href = '/abbonamento';
  };

  if (loading) {
    return children; // Show content while loading
  }

  return (
    <>
      {children}
      
      {/* Trial Expired Paywall - Cannot be dismissed */}
      <Dialog open={showPaywall} onOpenChange={() => {}}>
        <DialogContent className="bg-[#0a0a0a] border-[#FF5F1F] w-[340px] [&>button]:hidden" style={{padding: '16px', gap: 0}}>
          <div>
            <div className="text-center mb-2">
              <Crown className="w-8 h-8 text-[#FF5F1F] mx-auto mb-1" />
              <h2 className="text-white font-['Teko'] text-lg uppercase">
                Prova Terminata
              </h2>
            </div>

            <div className="space-y-1.5 mb-3">
              <div className="bg-[#121212] px-3 py-2 rounded border border-[#262626] flex items-center justify-between text-sm">
                <span className="text-white">Mensile</span>
                <span className="text-white font-mono font-bold">2 CHF/mese</span>
              </div>
              <div className="bg-[#121212] px-3 py-2 rounded border-2 border-[#FF5F1F] flex items-center justify-between text-sm">
                <span className="text-white">Annuale <span className="text-[#FF5F1F] text-xs">-33%</span></span>
                <span className="text-white font-mono font-bold">18 CHF/anno</span>
              </div>
            </div>

            <button 
              onClick={handleGoToSubscription}
              className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white font-bold py-2.5 rounded text-sm"
            >
              SCEGLI IL TUO PIANO
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Hook to get trial status
export function useTrialStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await subscriptionApi.getTrialStatus();
        setStatus(response.data);
      } catch (error) {
        console.error('Error fetching trial status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  return { status, loading };
}
